import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSignals } from "@preact/signals-react/runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format, parseISO, startOfWeek } from "date-fns";
import { memo, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import Button from "@ingka/button";
import SegmentedControl from "@ingka/segmented-control";
import ChevronLeftIcon from "@ingka/ssr-icon/paths/chevron-left-small";
import ChevronRightIcon from "@ingka/ssr-icon/paths/chevron-right-small";
import Text from "@ingka/text";
import { useBoolean } from "@pdpp/lib-react";
import { prefetchDayForecast, prefetchWeekForecast, prefetchYearForecast, } from "../../api/forecastApi";
import { countryCode$ } from "../../models/country";
import { selectedForecastDate$ } from "../../state/forecastDate";
import { selectedForecastView$ } from "../../state/forecastView";
import { weekStartsOn$ } from "../../state/weekStartsOn";
import { FISCAL_YEAR_MILLENNIUM, FISCAL_YEAR_START_MONTH, addDaysToDate, computeForecastBounds, doesWeekOverlapForecastRange, formatDateDisplay, formatDateShort, getCurrentFiscalYear, getFiscalYearDetails, getFiscalYearFromDate, getFiscalYearFullFromDate, getFiscalYearPeriod, getLocalWeekNumber, getWeekRange, isDateWithinForecastRange, } from "../../utils/forecastDateUtils";
import { getFiscalWeekStartKeyForDateKey, getNextFiscalWeekStartKey, getPreviousFiscalWeekStartKey, } from "../../utils/forecastTableWeekUtils";
import { AccuracyIndicator } from "./AccuracyIndicator";
import "./ControlBar.css";
import { DatePicker } from "./DatePicker";
const VIEWS = ["day", "week", "year"];
export const ControlBar = memo(function ControlBar$({ forecastApi, selectedDriver, unitId, hasValidDriverName, weekStartDate, accuracy, }) {
    useSignals();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isPickerOpen, , hidePicker, togglePicker] = useBoolean();
    const navButtonRef = useRef(null);
    const currentView = selectedForecastView$.value;
    const currentDate = selectedForecastDate$.value;
    const selectedDriverName = selectedDriver?.name;
    const selectedDriverSubType = selectedDriver?.subType;
    const formattedDate = useMemo(() => formatDateDisplay(currentDate), [currentDate]);
    const formattedDateShort = useMemo(() => formatDateShort(currentDate), [currentDate]);
    const previousDay = useMemo(() => addDaysToDate(currentDate, -1), [currentDate]);
    const nextDay = useMemo(() => addDaysToDate(currentDate, 1), [currentDate]);
    const isPreviousDayDisabled = useMemo(() => !isDateWithinForecastRange(previousDay), [previousDay]);
    const isNextDayDisabled = useMemo(() => !isDateWithinForecastRange(nextDay), [nextDay]);
    const handlePreviousDay = () => {
        if (isPreviousDayDisabled)
            return;
        const newDate = addDaysToDate(currentDate, -1);
        selectedForecastDate$.value = newDate;
    };
    const handleNextDay = () => {
        if (isNextDayDisabled)
            return;
        const newDate = addDaysToDate(currentDate, 1);
        selectedForecastDate$.value = newDate;
    };
    const handlePreviousDayHover = () => {
        if (currentView !== "day" || !hasValidDriverName || unitId == null)
            return;
        if (isPreviousDayDisabled)
            return;
        const previousDate = addDaysToDate(currentDate, -1);
        prefetchDayForecast(queryClient, forecastApi, {
            unitId,
            date: previousDate,
            driverName: selectedDriverName,
            driverSubType: selectedDriverSubType,
        });
    };
    const handleNextDayHover = () => {
        if (currentView !== "day" || !hasValidDriverName || unitId == null)
            return;
        if (isNextDayDisabled)
            return;
        const nextDate = addDaysToDate(currentDate, 1);
        prefetchDayForecast(queryClient, forecastApi, {
            unitId,
            date: nextDate,
            driverName: selectedDriverName,
            driverSubType: selectedDriverSubType,
        });
    };
    const effectiveWeekStartDate = weekStartDate ?? currentDate;
    const weekStartsOnValue = weekStartsOn$.value;
    const fiscalWeekAnchor = useMemo(() => {
        if (!effectiveWeekStartDate)
            return "";
        return getFiscalWeekStartKeyForDateKey(effectiveWeekStartDate, weekStartsOnValue);
    }, [effectiveWeekStartDate, weekStartsOnValue]);
    const previousFiscalWeekKey = useMemo(() => {
        if (!effectiveWeekStartDate)
            return null;
        return getPreviousFiscalWeekStartKey(effectiveWeekStartDate, weekStartsOnValue);
    }, [effectiveWeekStartDate, weekStartsOnValue]);
    const nextFiscalWeekKey = useMemo(() => {
        if (!effectiveWeekStartDate)
            return null;
        return getNextFiscalWeekStartKey(effectiveWeekStartDate, weekStartsOnValue);
    }, [effectiveWeekStartDate, weekStartsOnValue]);
    const codeValue = countryCode$.value;
    const countryCode = codeValue && codeValue.trim() !== ""
        ? codeValue.toUpperCase()
        : null;
    const weekRange = useMemo(() => {
        if (!effectiveWeekStartDate)
            return "";
        const { start, end } = getWeekRange(effectiveWeekStartDate);
        const year = (fiscalWeekAnchor ?? effectiveWeekStartDate).slice(0, 4);
        return `${start} – ${end} ${year}`;
    }, [effectiveWeekStartDate, fiscalWeekAnchor]);
    const weekNumber = useMemo(() => {
        if (!fiscalWeekAnchor)
            return "";
        const weekNum = getLocalWeekNumber(fiscalWeekAnchor);
        return `${t("labels.week")} ${weekNum}`;
    }, [fiscalWeekAnchor, t]);
    const isPreviousWeekDisabled = useMemo(() => previousFiscalWeekKey == null ||
        !doesWeekOverlapForecastRange(previousFiscalWeekKey), [previousFiscalWeekKey]);
    const isNextWeekDisabled = useMemo(() => nextFiscalWeekKey == null ||
        !doesWeekOverlapForecastRange(nextFiscalWeekKey), [nextFiscalWeekKey]);
    const handlePreviousWeek = () => {
        if (isPreviousWeekDisabled || previousFiscalWeekKey == null)
            return;
        selectedForecastDate$.value = previousFiscalWeekKey;
    };
    const handleNextWeek = () => {
        if (isNextWeekDisabled || nextFiscalWeekKey == null)
            return;
        selectedForecastDate$.value = nextFiscalWeekKey;
    };
    const handlePreviousWeekHover = () => {
        if (currentView !== "week" || !hasValidDriverName || unitId == null)
            return;
        if (isPreviousWeekDisabled || previousFiscalWeekKey == null)
            return;
        prefetchWeekForecast(queryClient, forecastApi, {
            unitId,
            weekStartDate: previousFiscalWeekKey,
            driverName: selectedDriverName,
            driverSubType: selectedDriverSubType,
        });
    };
    const handleNextWeekHover = () => {
        if (currentView !== "week" || !hasValidDriverName || unitId == null)
            return;
        if (isNextWeekDisabled || nextFiscalWeekKey == null)
            return;
        prefetchWeekForecast(queryClient, forecastApi, {
            unitId,
            weekStartDate: nextFiscalWeekKey,
            driverName: selectedDriverName,
            driverSubType: selectedDriverSubType,
        });
    };
    const fiscalYear = getFiscalYearFromDate(currentDate);
    const { fyStart, fyEnd, selectedFiscalYear } = getFiscalYearDetails(fiscalYear);
    const selectedFiscalYearFull = useMemo(() => FISCAL_YEAR_MILLENNIUM + selectedFiscalYear, [selectedFiscalYear]);
    const allowedFiscalYears = useMemo(() => {
        return computeForecastBounds().allowedFiscalYears;
    }, []);
    const isPrevYearDisabled = useMemo(() => !allowedFiscalYears.includes(selectedFiscalYearFull - 1), [selectedFiscalYearFull, allowedFiscalYears]);
    const isNextYearDisabled = useMemo(() => !allowedFiscalYears.includes(selectedFiscalYearFull + 1), [selectedFiscalYearFull, allowedFiscalYears]);
    const navigateToFiscalYear = (targetFiscalYear) => {
        const targetFiscalYearFull = FISCAL_YEAR_MILLENNIUM + targetFiscalYear;
        const currentFiscalYear = getCurrentFiscalYear();
        if (targetFiscalYearFull === currentFiscalYear) {
            selectedForecastDate$.value = format(new Date(), "yyyy-MM-dd");
        }
        else {
            const { fyStart: targetFyStart } = getFiscalYearPeriod(targetFiscalYear);
            selectedForecastDate$.value = format(targetFyStart, "yyyy-MM-dd");
        }
    };
    const handlePrevYear = () => {
        if (isPrevYearDisabled)
            return;
        navigateToFiscalYear(selectedFiscalYear - 1);
    };
    const handleNextYear = () => {
        if (isNextYearDisabled)
            return;
        navigateToFiscalYear(selectedFiscalYear + 1);
    };
    const handlePreviousYearHover = () => {
        if (currentView !== "year" ||
            !hasValidDriverName ||
            isPrevYearDisabled ||
            unitId == null)
            return;
        prefetchYearForecast(queryClient, forecastApi, {
            unitId,
            fiscalYear: selectedFiscalYear - 1,
            driverName: selectedDriverName,
            driverSubType: selectedDriverSubType,
        });
    };
    const handleNextYearHover = () => {
        if (currentView !== "year" ||
            !hasValidDriverName ||
            isNextYearDisabled ||
            unitId == null)
            return;
        prefetchYearForecast(queryClient, forecastApi, {
            unitId,
            fiscalYear: selectedFiscalYear + 1,
            driverName: selectedDriverName,
            driverSubType: selectedDriverSubType,
        });
    };
    const isPreviousButtonDisabled = () => {
        if (currentView === "day")
            return isPreviousDayDisabled;
        if (currentView === "week")
            return isPreviousWeekDisabled;
        if (currentView === "year")
            return isPrevYearDisabled;
        return false;
    };
    const isNextButtonDisabled = () => {
        if (currentView === "day")
            return isNextDayDisabled;
        if (currentView === "week")
            return isNextWeekDisabled;
        if (currentView === "year")
            return isNextYearDisabled;
        return false;
    };
    const handleViewChange = (_e, index) => {
        const newView = VIEWS[index];
        if (!newView)
            return;
        if (currentView === "year" && (newView === "day" || newView === "week")) {
            const today = new Date();
            const currentFiscalYear = getCurrentFiscalYear();
            let viewedFiscalYear = currentFiscalYear;
            let selectedDate = null;
            if (currentDate) {
                selectedDate = parseISO(currentDate);
                viewedFiscalYear = getFiscalYearFullFromDate(selectedDate);
            }
            if (selectedDate && isDateWithinForecastRange(currentDate)) {
                const currentFy = getFiscalYearFullFromDate(selectedDate);
                if (currentFy === viewedFiscalYear) {
                    selectedForecastView$.value = newView;
                    return;
                }
            }
            if (countryCode != null) {
                if (viewedFiscalYear === currentFiscalYear) {
                    if (newView === "day") {
                        selectedForecastDate$.value = format(today, "yyyy-MM-dd");
                    }
                    else if (newView === "week") {
                        const weekStart = startOfWeek(today);
                        selectedForecastDate$.value = format(weekStart, "yyyy-MM-dd");
                    }
                }
                else {
                    const fiscalYearStart = new Date(viewedFiscalYear - 1, FISCAL_YEAR_START_MONTH, 1);
                    if (newView === "day") {
                        selectedForecastDate$.value = format(fiscalYearStart, "yyyy-MM-dd");
                    }
                    else if (newView === "week") {
                        const weekStart = startOfWeek(fiscalYearStart);
                        selectedForecastDate$.value = format(weekStart, "yyyy-MM-dd");
                    }
                }
            }
            else {
                console.warn("countryCode is not set, using fallback date for view change");
                const fallbackBaseDate = viewedFiscalYear === currentFiscalYear
                    ? today
                    : new Date(viewedFiscalYear - 1, FISCAL_YEAR_START_MONTH, 1);
                if (newView === "day" || newView === "week") {
                    selectedForecastDate$.value = format(fallbackBaseDate, "yyyy-MM-dd");
                }
            }
        }
        selectedForecastView$.value = newView;
    };
    const handleDateSelected = (date) => {
        if (currentView === "day" && !isDateWithinForecastRange(date)) {
            return;
        }
        if (currentView === "week" && !doesWeekOverlapForecastRange(date)) {
            return;
        }
        selectedForecastDate$.value = date;
        hidePicker();
    };
    const items = useMemo(() => [
        { label: t("labels.day") },
        { label: t("labels.week") },
        { label: t("labels.year") },
    ], [t]);
    const activeIndex = useMemo(() => {
        const index = VIEWS.indexOf(currentView);
        return index >= 0 ? index : 0;
    }, [currentView]);
    const renderDateDisplay = () => {
        switch (currentView) {
            case "day":
                return (_jsx("div", { className: "forecast-control-bar__date", children: formattedDate }));
            case "week":
                return (_jsx("div", { className: "forecast-control-bar__date", children: weekRange || t("labels.loading") }));
            case "year": {
                const startLabel = format(fyStart, "d MMM yyyy");
                const endLabel = format(fyEnd, "d MMM yyyy");
                return (_jsxs("div", { className: "forecast-control-bar__date", children: [startLabel, " \u2013 ", endLabel] }));
            }
            default:
                return (_jsx("div", { className: "forecast-control-bar__date", children: t("labels.loading") }));
        }
    };
    const renderNavigationButtons = () => {
        const renderButtonGroup = (middleButtonText, onPrevious, onNext, onPreviousHover = () => { }, onNextHover = () => { }, previousAriaLabel, nextAriaLabel) => {
            return (_jsxs("div", { className: "forecast-control-bar__buttons", children: [_jsx(Button, { onClick: onPrevious, onMouseEnter: onPreviousHover, disabled: isPreviousButtonDisabled(), size: "small", iconOnly: true, ssrIcon: ChevronLeftIcon, "aria-label": previousAriaLabel }), _jsx(Button, { className: "forecast-control-bar__nav_btn", size: "small", ref: navButtonRef, onClick: togglePicker, "aria-haspopup": "dialog", "aria-expanded": isPickerOpen, children: middleButtonText }), _jsx(Button, { onClick: onNext, onMouseEnter: onNextHover, disabled: isNextButtonDisabled(), size: "small", iconOnly: true, ssrIcon: ChevronRightIcon, "aria-label": nextAriaLabel })] }));
        };
        switch (currentView) {
            case "day":
                return renderButtonGroup(formattedDateShort, handlePreviousDay, handleNextDay, handlePreviousDayHover, handleNextDayHover, t("controlBar.previousDay"), t("controlBar.nextDay"));
            case "week":
                return renderButtonGroup(weekNumber, handlePreviousWeek, handleNextWeek, handlePreviousWeekHover, handleNextWeekHover, t("controlBar.previousWeek"), t("controlBar.nextWeek"));
            case "year": {
                const fiscalYearLabel = `FY ${String(selectedFiscalYear).padStart(2, "0")}`;
                return renderButtonGroup(fiscalYearLabel, handlePrevYear, handleNextYear, handlePreviousYearHover, handleNextYearHover, t("controlBar.previousYear"), t("controlBar.nextYear"));
            }
            default:
                return _jsx("div", { className: "forecast-control-bar__buttons" });
        }
    };
    return (_jsxs("header", { className: "forecast-control-bar", children: [_jsx(Text, { textStyle: "Heading.M", children: renderDateDisplay() }), _jsxs("div", { className: "forecast-control-bar__right", children: [_jsx(AccuracyIndicator, { accuracy: accuracy ?? null }), _jsx(SegmentedControl, { activeIndex: activeIndex, fluid: true, size: "small", subtle: true, className: "forecast-control-bar__segmented", items: items, onClick: handleViewChange }), renderNavigationButtons()] }), isPickerOpen && navButtonRef.current && (_jsx(DatePicker, { mode: currentView, selectedDate: currentDate, onSelect: handleDateSelected, onClose: hidePicker, anchorEl: navButtonRef.current, clickAwayHandler: hidePicker, horizontalAlignment: "right", horizontalPosition: "right", verticalAlignment: "top", verticalPosition: "bottom", onScroll: hidePicker }))] }));
});
//# sourceMappingURL=ControlBar.js.map